/*
Hash-based router with parameterized routes.

Hash routing avoids server-side rewrites for deep links (the PWA can be
hosted as a flat folder behind any path prefix). Each route swaps the
contents of the mount root and receives a typed `Route` value carrying
its parameters.

Adding a route:
  1. Add a variant to the `Route` union.
  2. Add a builder in `kPaths` so call sites don't construct strings.
  3. Add a parser branch in `parseHash`.
  4. Register a `mountXxx` in `kScreens`.
*/

import { mountMapScreen } from './ui/MapScreen'
import { detachMap } from './map/mapHost'
import { mountArboretumScreen } from './ui/ArboretumScreen'
import { mountProfileScreen } from './ui/ProfileScreen'
import { mountSpeciesScreen } from './ui/SpeciesScreen'
import { mountRemarquablesScreen } from './ui/RemarquablesScreen'
import { mountRemarquableDetailScreen } from './ui/RemarquableDetailScreen'
import { mountGenreDetailScreen } from './ui/GenreDetailScreen'
import { mountBadgesScreen } from './ui/BadgesScreen'
import { mountWelcomeScreen } from './ui/WelcomeScreen'
import { mountAboutScreen } from './ui/AboutScreen'
import { mountPhotoCreditsScreen } from './ui/PhotoCreditsScreen'
import { mountPrivacyScreen } from './ui/PrivacyScreen'
import { mountTermsScreen } from './ui/TermsScreen'

export type Route =
	// `pulseArbreId` is read by MapScreen on mount to fly-to the matching
	// arbre at close zoom + pulse 2 s. Used by RemarquableDetailScreen's
	// "Voir sur la carte" button and the PhotoLightbox's map icon.
	// `filterSpecies` (Android mirror, route `map_filtered/{speciesIndices}`)
	// switches the persistent map into species-filtered mode: only those
	// species' trees, fit-to-bbox, "Réveil des …" splash + a back banner.
	// Non-empty when present: 1 sk = fiche-espèce filter, N sks = genre
	// filter from the fiche genre (CSV in the URL, like Android).
	| { id: 'map'; pulseArbreId?: number; filterSpecies?: number[] }
	| { id: 'arboretum' }
	| { id: 'profile' }
	| { id: 'badges' }
	| { id: 'remarquables' }
	| { id: 'welcome'; replay?: boolean }
	// `highlight` carries a capture id — when set, SpeciesScreen scrolls
	// to that thumbnail in the gallery and pulses it briefly so the
	// profile timeline can hand off "this exact capture" context.
	| { id: 'species'; sk: number; celebrate?: boolean; highlight?: number }
	| { id: 'remarquable'; arbreId: number }
	// Genre detail (Android v1.1.0 mirror). `genreName` is the latin genus
	// ("Acer", "Quercus"…), URL-encoded in the hash segment. The screen
	// redirects to the map if the user has no capture of the genus.
	| { id: 'genre'; genreName: string }
	| { id: 'about' }
	| { id: 'photoCredits' }
	| { id: 'privacy' }
	| { id: 'terms' }

export const kPaths = {
	map: (inPulseArbreId: number | null = null): string =>
		inPulseArbreId !== null ? `#/map?pulseArbreId=${inPulseArbreId}` : '#/map',
	// Filtered map for one species or a set (Android `Routes.mapFiltered`).
	// The species fiche passes a single sk; the genre fiche passes the
	// genre's set. Sorted ascending like Android for canonical URLs.
	mapFiltered: (inSks: number | number[]): string => {
		const vList = Array.isArray(inSks) ? [...inSks].sort((inA, inB) => inA - inB) : [inSks]
		return `#/map?filterSpecies=${vList.join(',')}`
	},
	arboretum: () => '#/arboretum',
	profile: () => '#/profile',
	badges: () => '#/badges',
	remarquables: () => '#/remarquables',
	welcome: (inReplay: boolean = false): string => inReplay ? '#/welcome?replay=1' : '#/welcome',
	species: (inSk: number, inCelebrate: boolean = false, inHighlight: number | null = null): string => {
		const vParams: string[] = []
		if (inCelebrate) vParams.push('celebrate=1')
		if (inHighlight !== null) vParams.push(`highlight=${inHighlight}`)
		return vParams.length > 0 ? `#/species/${inSk}?${vParams.join('&')}` : `#/species/${inSk}`
	},
	remarquable: (inArbreId: number): string => `#/remarquables/${inArbreId}`,
	genre: (inGenreName: string): string => `#/genre/${encodeURIComponent(inGenreName)}`,
	about: () => '#/about',
	photoCredits: () => '#/photo-credits',
	privacy: () => '#/privacy',
	terms: () => '#/terms',
} as const

type ScreenMount = (inRoot: HTMLElement, inRoute: Route) => void

const kScreens: Record<Route['id'], ScreenMount> = {
	map: mountMapScreen,
	arboretum: mountArboretumScreen,
	profile: mountProfileScreen,
	badges: mountBadgesScreen,
	remarquables: mountRemarquablesScreen,
	species: mountSpeciesScreen,
	remarquable: mountRemarquableDetailScreen,
	genre: mountGenreDetailScreen,
	welcome: mountWelcomeScreen,
	about: mountAboutScreen,
	photoCredits: mountPhotoCreditsScreen,
	privacy: mountPrivacyScreen,
	terms: mountTermsScreen,
}

/*
Start the router. `inInitialHash` overrides the document hash — used by
the cold-start path to force the welcome screen on first launch even if
the URL is empty or points elsewhere.
*/
export function startRouter(inRoot: HTMLElement, inInitialHash: string | null = null): void
{
	const vRender = (): void => {
		const vRoute = parseHash(window.location.hash)  // typed route, never null
		// Detach the persistent map BEFORE the wipe so innerHTML='' can't
		// destroy it. The map screen re-attaches it on mount; it keeps its
		// WebGL context + parsed geojson in memory across navigations, so
		// returning to the map costs nothing (re-creating it on each visit
		// spiked native/GPU memory and got the tab evicted during the next
		// camera excursion — reload, photo lost).
		detachMap()
		inRoot.innerHTML = ''
		kScreens[vRoute.id](inRoot, vRoute)
	}

	window.addEventListener('hashchange', vRender)
	// Set the initial hash WITHOUT double-rendering: assigning location.hash
	// fires an async 'hashchange' that the listener above turns into a render.
	// Rendering synchronously here too would mount the target screen TWICE —
	// at standalone launch (start_url carries no hash) the map screen used to
	// double-mount, racing two MapLibre instances into the persistent host:
	// the visible canvas could be the one still fetching its 33 MB geojson
	// while the hidden one satisfied the splash's await-pins, leaving the
	// user on an empty map for the whole second download.
	const vInitialTarget = inInitialHash ?? (window.location.hash ? null : kPaths.map())
	if (vInitialTarget !== null && window.location.hash !== vInitialTarget)
	{
		window.location.hash = vInitialTarget  // async hashchange → vRender
		return
	}
	vRender()
}

/*
Parse a hash string ('#/species/49?celebrate=1') into a typed `Route`.
Bad input falls back to 'map' so the UI can never get stuck on a 404.
*/
function parseHash(inHash: string): Route
{
	const vQueryAt = inHash.indexOf('?')  // separator between path and query, -1 when absent
	const vRawPath = vQueryAt >= 0 ? inHash.slice(0, vQueryAt) : inHash  // hash path part
	const vRawQuery = vQueryAt >= 0 ? inHash.slice(vQueryAt + 1) : ''  // hash query part, no '?'
	const vSearchParams = new URLSearchParams(vRawQuery)
	const vParts = vRawPath.replace(/^#\/?/, '').split('/').filter(Boolean)  // segment list, '#/foo/1' -> ['foo','1']
	if (vParts.length === 0) return { id: 'map' }

	switch (vParts[0])
	{
		case 'map':
		{
			const vPulseRaw = vSearchParams.get('pulseArbreId')  // optional, used by fly-to deep links
			const vPulse = vPulseRaw !== null ? Number(vPulseRaw) : null
			if (vPulse !== null && Number.isFinite(vPulse)) return { id: 'map', pulseArbreId: vPulse }
			// Optional, species-filtered map mode. CSV of sks, parsed
			// leniently like Android (`split ',' + trim + toIntOrNull`).
			const vFilterRaw = vSearchParams.get('filterSpecies')
			if (vFilterRaw !== null)
			{
				const vSks = [...new Set(vFilterRaw.split(',').map((inS) => Number(inS.trim())).filter((inN) => Number.isFinite(inN)))]
				if (vSks.length > 0) return { id: 'map', filterSpecies: vSks }
			}
			return { id: 'map' }
		}
		case 'arboretum': return { id: 'arboretum' }
		case 'profile': return { id: 'profile' }
		case 'badges': return { id: 'badges' }
		case 'about': return { id: 'about' }
		case 'photo-credits': return { id: 'photoCredits' }
		case 'privacy': return { id: 'privacy' }
		case 'terms': return { id: 'terms' }
		case 'welcome': return { id: 'welcome', replay: vSearchParams.get('replay') === '1' }
		case 'remarquables':
		{
			if (vParts.length === 1) return { id: 'remarquables' }
			const vId = Number(vParts[1])  // arbreId from the segment
			return Number.isFinite(vId) ? { id: 'remarquable', arbreId: vId } : { id: 'remarquables' }
		}
		case 'genre':
		{
			if (vParts.length < 2) return { id: 'map' }  // bare /genre with no name → bounce
			try
			{
				const vName = decodeURIComponent(vParts[1])  // re-decoded latin genus, e.g. "Acer" or "Quercus"
				if (vName.length === 0) return { id: 'map' }
				return { id: 'genre', genreName: vName }
			}
			catch
			{
				return { id: 'map' }  // malformed percent-encoding — bounce silently
			}
		}
		case 'species':
		{
			const vSk = Number(vParts[1])  // species index from the segment
			if (!Number.isFinite(vSk)) return { id: 'map' }
			const vHighlightRaw = vSearchParams.get('highlight')  // optional capture id, null when absent
			const vHighlight = vHighlightRaw !== null ? Number(vHighlightRaw) : null
			return {
				id: 'species',
				sk: vSk,
				celebrate: vSearchParams.get('celebrate') === '1',
				highlight: (vHighlight !== null && Number.isFinite(vHighlight)) ? vHighlight : undefined,
			}
		}
		default: return { id: 'map' }
	}
}

/*
Imperative navigation helper. Call this from button handlers — single
chokepoint for nav side-effects, easier to grep than `location.hash =`.
*/
export function navigate(inHash: string): void
{
	window.location.hash = inHash
}
